import { useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Bell, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { noticesAPI } from '../../lib/api';

const NoticesTab = ({ notices, onMarkRead }) => {
  useEffect(() => {
    // Full notice content is displayed, so viewing the tab counts as reading
    notices.filter(n => !n.is_read).forEach(n => handleMarkNoticeRead(n.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notices]);

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent':
        return <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />;
      case 'important':
        return <Info className="w-5 h-5 text-yellow-500 flex-shrink-0" />;
      default:
        return <Bell className="w-5 h-5 text-blue-500 flex-shrink-0" />;
    }
  };

  const handleMarkNoticeRead = async (noticeId) => {
    try {
      await noticesAPI.markRead(noticeId);
      onMarkRead?.(noticeId);
    } catch (error) {
      console.error('Failed to mark notice as read:', error);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
        <Bell className="w-5 h-5" />
        School Noticeboard
      </h3>
      
      {notices.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Bell className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
            <p className="text-neutral-500">No notices at the moment</p>
          </CardContent>
        </Card>
      ) : (
        notices.map(notice => (
          <Card 
            key={notice.id} 
            data-testid={`notice-card-${notice.id}`}
            className={`transition-all bg-neutral-900 border-neutral-700 text-white ${!notice.is_read ? 'border-l-4 border-l-blue-500' : ''}`}
            onClick={() => !notice.is_read && handleMarkNoticeRead(notice.id)}
          >
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                {getPriorityIcon(notice.priority)}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-white">{notice.title}</h4>
                    {!notice.is_read && (
                      <Badge className="bg-blue-100 text-blue-700 text-xs">New</Badge>
                    )}
                    {notice.priority === 'urgent' && (
                      <Badge className="bg-red-100 text-red-700 text-xs">Urgent</Badge>
                    )}
                    {notice.priority === 'important' && (
                      <Badge className="bg-yellow-100 text-yellow-700 text-xs">Important</Badge>
                    )}
                  </div>
                  <p className="text-neutral-200 whitespace-pre-wrap">{notice.content}</p>
                  <p className="text-xs text-neutral-400 mt-2">
                    Posted by {notice.created_by_name} • {new Date(notice.created_at).toLocaleDateString('en-GB')}
                  </p>
                </div>
                {notice.is_read && (
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                )}
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default NoticesTab;
